import { useCallback, useMemo } from 'react';
import { useLazyQuery, type QueryResult } from '@apollo/client';
import {
  GetInspectionTemplateDocument,
  type GetInspectionTemplateQuery,
  type GetInspectionTemplateQueryVariables,
} from 'graphql-query-contracts';
import type { InspectionTemplate } from 'inspection-types';

import { mapGraphQLTemplateToDomain } from './mappers';
import { InvalidInspectionTemplateParamsError } from './errors/InvalidInspectionTemplateParamsError';

export type FetchInspectionTemplateParams = {
  id: string;
  channelId: string;
  version?: number | null;
};

export type FetchInspectionTemplateResult = Promise<
  QueryResult<GetInspectionTemplateQuery, GetInspectionTemplateQueryVariables>
>;

export type UseGetInspectionTemplateResult = {
  template: InspectionTemplate | undefined;
  loading: boolean;
  error: Error | undefined;
  fetchInspectionTemplate: (
    params: FetchInspectionTemplateParams
  ) => FetchInspectionTemplateResult;
  called: boolean;
};

function isBlank(value: string | undefined | null): boolean {
  return value === undefined || value === null || value.trim() === '';
}

export function useGetInspectionTemplate(): UseGetInspectionTemplateResult {
  const [fetchTemplate, { loading, data, error, called }] = useLazyQuery(
    GetInspectionTemplateDocument,
    {
      fetchPolicy: 'network-only',
    }
  );

  const fetchInspectionTemplate = useCallback(
    ({
      id,
      channelId,
      version,
    }: FetchInspectionTemplateParams): FetchInspectionTemplateResult => {
      if (isBlank(id) || isBlank(channelId)) {
        throw new InvalidInspectionTemplateParamsError();
      }

      return fetchTemplate({
        variables: {
          id,
          channelId,
          version: version ?? undefined,
        },
      });
    },
    [fetchTemplate]
  );

  const template = useMemo(() => {
    const gqlTemplate = data?.inspectionTemplate;

    if (!gqlTemplate) {
      return undefined;
    }

    return mapGraphQLTemplateToDomain(gqlTemplate);
  }, [data]);

  return {
    template,
    loading,
    error: error ?? undefined,
    fetchInspectionTemplate,
    called,
  };
}
